import React from 'react';
import { Link } from 'react-router-dom';
import { useSystemSettings, defaultSystemSettings } from '../contexts/SystemSettingsContext';
import { Zap, Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram } from 'lucide-react';

interface FooterLink {
  name: string;
  href: string;
}

interface SocialLink {
  platform: string;
  url: string;
}

const socialIconMap: Record<string, React.FC<{ className?: string }>> = {
  linkedin: Linkedin,
  twitter: Twitter,
  facebook: Facebook,
  instagram: Instagram,
};

const Footer: React.FC = () => {
  const { settings } = useSystemSettings();

  const footerBrandName = settings?.footerBrandName || defaultSystemSettings.footerBrandName;
  const footerDescription = settings?.footerDescription || defaultSystemSettings.footerDescription;
  const footerContactEmail = settings?.footerContactEmail || defaultSystemSettings.footerContactEmail;
  const footerContactPhone = settings?.footerContactPhone || defaultSystemSettings.footerContactPhone;
  const footerContactAddress = settings?.footerContactAddress || defaultSystemSettings.footerContactAddress;
  const footerCopyrightText = settings?.footerCopyrightText || defaultSystemSettings.footerCopyrightText;

  let productLinks: FooterLink[] = [];
  let resourceLinks: FooterLink[] = [];
  let companyLinks: FooterLink[] = [];
  let socialLinks: SocialLink[] = [];

  try {
    productLinks = JSON.parse(settings?.footerProductLinks || defaultSystemSettings.footerProductLinks);
  } catch { productLinks = []; }

  try {
    resourceLinks = JSON.parse(settings?.footerResourceLinks || defaultSystemSettings.footerResourceLinks);
  } catch { resourceLinks = []; }

  try {
    companyLinks = JSON.parse(settings?.footerCompanyLinks || defaultSystemSettings.footerCompanyLinks);
  } catch { companyLinks = []; }

  try {
    socialLinks = JSON.parse(settings?.footerSocialLinks || defaultSystemSettings.footerSocialLinks);
  } catch { socialLinks = []; }

  return (
    <footer className="bg-emerald-950 text-white relative z-10 mt-auto border-t border-emerald-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold">{footerBrandName}</span>
            </Link>
            <p className="text-white/70 mb-6 max-w-sm">{footerDescription}</p>
            <div className="space-y-3 text-white/70">
              <div className="flex items-center gap-3"><Mail className="w-5 h-5" /><span>{footerContactEmail}</span></div>
              <div className="flex items-center gap-3"><Phone className="w-5 h-5" /><span>{footerContactPhone}</span></div>
              <div className="flex items-center gap-3"><MapPin className="w-5 h-5" /><span>{footerContactAddress}</span></div>
            </div>
          </div>
          {productLinks.length > 0 && (
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-3">
                {productLinks.map((link) => (
                  <li key={link.name}><Link to={link.href} className="text-emerald-200/80 hover:text-white transition-colors">{link.name}</Link></li>
                ))}
              </ul>
            </div>
          )}
          {resourceLinks.length > 0 && (
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-3">
                {resourceLinks.map((link) => (
                  <li key={link.name}><a href={link.href} className="text-emerald-200/80 hover:text-white transition-colors">{link.name}</a></li>
                ))}
              </ul>
            </div>
          )}
          {companyLinks.length > 0 && (
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-3">
                {companyLinks.map((link) => (
                  <li key={link.name}><a href={link.href} className="text-emerald-200/80 hover:text-white transition-colors">{link.name}</a></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-emerald-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-emerald-200/70 text-sm">{footerCopyrightText}</p>
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-4">
                {socialLinks.map((social) => {
                  const IconComponent = socialIconMap[social.platform.toLowerCase()] || Zap;
                  return (
                    <a key={social.platform} href={social.url} className="w-10 h-10 rounded-full bg-emerald-800/50 flex items-center justify-center hover:bg-emerald-700/50 transition-colors" aria-label={social.platform}>
                      <IconComponent className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;